import { useState, useMemo } from "react";
import { JSONTree } from "react-json-tree";
import type { RoomSnapshot, SourceSnapshot } from "@tuit/shared-types";
import styles from "../../styles/components/AdminPage.module.css";
import { relativeTime } from "./utils";

type SortCol = "source" | "fetchedAt" | "items";
type SortDir = "asc" | "desc";
type FetchStatus = SourceSnapshot["fetchStatus"];

interface Props {
  snapshot: RoomSnapshot | null;
  roomId: string;
  onSelectSource: (id: string) => void;
}

function SortTh({
  col,
  sort,
  onSort,
  children,
}: {
  col: SortCol;
  sort: { col: SortCol; dir: SortDir };
  onSort: (col: SortCol) => void;
  children: React.ReactNode;
}) {
  const active = sort.col === col;
  return (
    <th
      className={`${styles.thSortable} ${active ? styles.thActive : ""}`}
      onClick={() => onSort(col)}
    >
      {children}
      <span className={styles.sortArrow}>
        {active ? (sort.dir === "asc" ? " ↑" : " ↓") : " ↕"}
      </span>
    </th>
  );
}

function RoomTableContent({ snapshot, onSelectSource }: { snapshot: RoomSnapshot; onSelectSource: (id: string) => void }) {
  const sources = snapshot.sourceSnapshots;
  const [sort, setSort] = useState<{ col: SortCol; dir: SortDir }>({
    col: "source",
    dir: "asc",
  });
  const [statusFilter, setStatusFilter] = useState<Set<FetchStatus>>(new Set());

  function handleSort(col: SortCol) {
    setSort((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { col, dir: "asc" },
    );
  }

  function toggleFilter(status: FetchStatus) {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  }

  const counts = {
    ok: sources.filter((s) => s.fetchStatus === "ok").length,
    error: sources.filter((s) => s.fetchStatus === "error").length,
    never: sources.filter((s) => s.fetchStatus === "never").length,
  };

  const displayed = useMemo(() => {
    const filtered =
      statusFilter.size === 0
        ? sources
        : sources.filter((s) => statusFilter.has(s.fetchStatus));
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sort.col) {
        case "source":
          cmp = a.sourceId.localeCompare(b.sourceId);
          break;
        case "fetchedAt":
          cmp =
            new Date(a.fetchedAt).getTime() - new Date(b.fetchedAt).getTime();
          break;
        case "items":
          cmp = a.items.length - b.items.length;
          break;
      }
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [sources, sort, statusFilter]);

  return (
    <div>
      <div className={styles.pills}>
        <button
          type="button"
          className={`${styles.pill} ${styles.pillOk} ${statusFilter.has("ok") ? styles.pillFilled : styles.pillHollow}`}
          onClick={() => toggleFilter("ok")}
        >
          {counts.ok} OK
        </button>
        <button
          type="button"
          className={`${styles.pill} ${styles.pillError} ${statusFilter.has("error") ? styles.pillFilled : styles.pillHollow}`}
          onClick={() => toggleFilter("error")}
        >
          {counts.error} Error
        </button>
        <button
          type="button"
          className={`${styles.pill} ${styles.pillNever} ${statusFilter.has("never") ? styles.pillFilled : styles.pillHollow}`}
          onClick={() => toggleFilter("never")}
        >
          {counts.never} Never fetched
        </button>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.thPip}>
              <span className={styles.srOnly}>Status</span>
            </th>
            <th>ID</th>
            <SortTh col="source" sort={sort} onSort={handleSort}>
              Source
            </SortTh>
            <SortTh col="fetchedAt" sort={sort} onSort={handleSort}>
              Fetched
            </SortTh>
            <SortTh col="items" sort={sort} onSort={handleSort}>
              Items
            </SortTh>
            <th>Error</th>
          </tr>
        </thead>
        <tbody>
          {displayed.map((s) => (
            <tr key={s.sourceId} className={styles[`row_${s.fetchStatus}`]}>
              <td>
                <span
                  className={`${styles.pip} ${styles[`pip_${s.fetchStatus}`]}`}
                />
              </td>
              <td>
                <button type="button" className={styles.clickableId} onClick={() => onSelectSource(s.sourceId)}>
                  {s.sourceId}
                </button>
              </td>
              <td>{s.title}</td>
              <td>{relativeTime(s.fetchedAt)}</td>
              <td>{s.items.length}</td>
              <td className={styles.errorCell}>{s.errorMessage ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RoomTable({ snapshot, roomId, onSelectSource }: Props) {
  const [view, setView] = useState<"ux" | "json">("ux");

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>
          Room Snapshot: {roomId}
          {snapshot && (
            <span className={styles.panelTitleMeta}>
              generated {relativeTime(snapshot.generatedAt)}
            </span>
          )}
        </h2>
        {snapshot && (
          <div className={styles.viewToggle}>
            {(["ux", "json"] as const).map((v) => (
              <button
                key={v}
                type="button"
                className={`${styles.viewToggleBtn} ${view === v ? styles.viewToggleBtnActive : ""}`}
                onClick={() => setView(v)}
              >
                {v.toUpperCase()}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className={styles.panelBody}>
        {!snapshot ? (
          <span className={styles.panelEmpty}>Load a Room to see details.</span>
        ) : view === "json" ? (
          <JSONTree data={snapshot} />
        ) : (
          <RoomTableContent snapshot={snapshot} onSelectSource={onSelectSource} />
        )}
      </div>
    </div>
  );
}
